import { createContext, useContext } from 'react';

/**
 * Seam between the Runtime Adapter and the DE ActionDispatcher
 * (DB-2.1 / EDUN-10689).
 *
 * This ticket (DB-1.4) only needs the `dispatch(type, params)` contract
 * so the binding enhancer can wire `_action.{event}` declarations to
 * real handlers. DB-2.1 ships the registry and concrete navigation /
 * form / API handlers.
 */
export interface ActionDispatcher {
  /**
   * Invoke the handler registered for `type`, passing pre-interpolated
   * `params`. Implementations must never throw — surface failures via
   * telemetry and fall through silently so one bad action does not
   * break the rendered step.
   */
  dispatch(type: string, params?: Readonly<Record<string, unknown>>): void;
  /**
   * Optional probe. When present, the enhancer can short-circuit on
   * unknown types (e.g. render a console warning without even building
   * a handler closure).
   */
  has?(type: string): boolean;
  /**
   * Drop pending local simulations. Follow-ups scheduled by
   * `stub.simulate` do not run after this. The dispatcher stays usable.
   */
  dispose?(): void;
  /**
   * Drop in-flight simulations without retiring the dispatcher. A named
   * modal closing uses this so a dismissed sign-in cannot still open
   * its outcome.
   */
  cancelPendingSimulations?(): void;
}

/**
 * Default dispatcher used when no provider wraps the render tree.
 * Warn-logs on every call so authoring mistakes surface early, but
 * never throws — the rendered step keeps working.
 */
export const NOOP_ACTION_DISPATCHER: ActionDispatcher = Object.freeze({
  dispatch(type: string, params?: Readonly<Record<string, unknown>>): void {
    // eslint-disable-next-line no-console
    console.warn(
      `[binding-enhancer] No ActionDispatcher provided; dropping dispatch("${type}")`,
      params,
    );
  },
  has(): boolean {
    return false;
  },
});

/**
 * React context carrying the active {@link ActionDispatcher}. Rendered
 * inside the Data Binding provider stack (child SAD ADR-14 — between
 * `ComparisonProvider` and `<Puck Render>`). Defaults to
 * {@link NOOP_ACTION_DISPATCHER} so standalone renders (tests, demos,
 * Storybook) still work without explicit wiring.
 */
export const ActionDispatcherContext = createContext<ActionDispatcher>(
  NOOP_ACTION_DISPATCHER,
);

/**
 * Hook returning the nearest {@link ActionDispatcher}. Falls back to
 * {@link NOOP_ACTION_DISPATCHER} outside a provider — matches the
 * context default so consumers never get `undefined`.
 */
export function useActionDispatcher(): ActionDispatcher {
  return useContext(ActionDispatcherContext);
}
